import "./App.css";
import { ConfigProvider, App as AntdApp } from "antd";
import { Routes, Route } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spin } from "antd";
import GeneralLayout from "./components/GeneralLayout";
import LoginPage from "./pages/auth/LoginPage";
import ProtectedRoute from "./components/ProtectedRoute";
import UserManagementProtectedRoute from "./components/UserManagementProtectedRoute";
import NotFoundRedirect from "./components/NotFoundRedirect";
import AdminCommandPalette from "./components/AdminCommandPalette";
import AdminHomePage from "./pages/AdminHomePage";
// Reuse web's hardened update prompt (safe-boundary auto-reload enforcer +
// escalation ladder). Store-independent, so it drops into admin's <AntdApp>
// unchanged; admin→web imports are the sanctioned direction.
import UpdateAvailableNotification from "@web/components/UpdateAvailableNotification";
// Render target for `useToast`. Without it every toast() call in the admin
// surfaces — success, error and warning alike — updates the store and is never
// drawn, so operations that report their outcome only via a toast appear to do
// nothing. apps/web mounts its own inside DashAIRoutes; admin needs this one.
import { Toaster } from "@dashai/components/ui/toaster";
import InternalConfigurationTablePage from "./pages/InternalConfigurationTablePage.jsx";
import UserScopeManagementPage from "./pages/UserScopeManagementPage.jsx";
import ApiKeysManagementPage from "./pages/ApiKeysManagementPage.jsx";
import InviteLinksManagementPage from "./pages/InviteLinksManagementPage.jsx";
import RefreshMonitorPage from "./pages/RefreshMonitorPage.jsx";
import EmailDeliveryLogPage from "./pages/EmailDeliveryLogPage.jsx";
import QaDashboardPage from "./pages/QaDashboardPage.jsx";
import KeyAccountsDailyConfigPage from "./pages/KeyAccountsDailyConfigPage.jsx";
import AuditTrailPage from "./pages/AuditTrailPage.jsx";
import CswapPage from "./pages/CswapPage.jsx";
import { startAuthListener } from "./slices/authSlice";

// Lazy-load heavy page to reduce initial bundle
// Three task-focused CASS pages: catalog, management, and mappings.
const CassCatalogPage = lazy(() => import("./pages/CassCatalogPage.jsx"));
const CassManagementPage = lazy(() => import("./pages/CassManagementPage.jsx"));
const CassMappingsPage = lazy(() => import("./pages/CassMappingsPage.jsx"));
const DeliveredFeaturesPage = lazy(() => import("./pages/DeliveredFeaturesPage.jsx"));
// Flattened DashAI admin sections (shadcn-styled; AdminShell wraps each in .dashai-scope).
const AdminShell = lazy(() => import("./features/dashai/components/admin/AdminShell.tsx"));
const AdminSectionPage = lazy(() => import("./features/dashai/components/admin/AdminSectionPage.tsx"));
const DashaiSuperAdmin = lazy(() => import("./features/dashai/pages/SuperAdmin.tsx"));
const LazyFallback = (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
    <Spin size="large" />
  </div>
);

function App() {
  const dispatch = useDispatch();
  const { isInitialized, user } = useSelector((state) => state.auth);
  useEffect(() => dispatch(startAuthListener()), [dispatch]);
  if (!isInitialized) return LazyFallback;

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: "#0E49B5" },
        components: {
          Segmented: {
            itemActiveBg: "rgba(255, 255, 255, 0.15)",
            trackBg: "transparent",
            itemColor: "#fff",
            itemHoverBg: "rgba(255, 255, 255, 0.15)",
            itemSelectedBg: "rgba(255, 255, 255, 0.15)",
            itemSelectedColor: "#fff",
            itemHoverColor: "#fff",
            trackPadding: "0.5rem",
          },
          Input: {
            activeBg: "trasparent",
            hoverBg: "transparent",
          },
          Table: {
            headerBg: "#fafafa",
            headerColor: "#27272a",
          },
          Tabs: {
            cardPadding: 0,
            itemActiveColor: "#fff",
            itemSelectedColor: "#fff",
            itemHoverColor: "#000",
            cardGutter: 0,
          },
          Tree: {
            indentSize: 0,
          },
        },
      }}
    >
      <AntdApp component={false}>
        <UpdateAvailableNotification />
        <Routes>
            <Route
              path="/login"
              element={<LoginPage />}
            />
            <Route
              path="/*"
              element={
                <ProtectedRoute key={user?.uid}>
                  {/* Mounted once for authenticated users so ⌘K works on every
                      page, Ant and DashAI alike. */}
                  <AdminCommandPalette />
                  <Routes>
                    {/* The landing page. Not wrapped in
                        UserManagementProtectedRoute: that guard redirects a
                        non-staff user here, so gating the target on it would
                        loop. AdminHomePage renders its own GeneralLayout. */}
                    <Route
                      index
                      path="/"
                      element={<AdminHomePage />}
                    />
                    {/* UM + Settings moved to the main app (apps/web). No redirects —
                        admin is an internal tool, so stale bookmarks can just dead-end. */}
                    <Route
                      path="/internal-configuration"
                      element={
                        <UserManagementProtectedRoute>
                          <InternalConfigurationTablePage />
                        </UserManagementProtectedRoute>
                      }
                    />
                    <Route path="/cswap" element={
                      <UserManagementProtectedRoute>
                        <GeneralLayout><CswapPage /></GeneralLayout>
                      </UserManagementProtectedRoute>
                    } />
                    <Route
                      path="/api-keys"
                      element={
                        <UserManagementProtectedRoute>
                          <GeneralLayout>
                            <ApiKeysManagementPage />
                          </GeneralLayout>
                        </UserManagementProtectedRoute>
                      }
                    />
                    <Route
                      path="/invite-links"
                      element={
                        <UserManagementProtectedRoute>
                          <GeneralLayout>
                            <InviteLinksManagementPage />
                          </GeneralLayout>
                        </UserManagementProtectedRoute>
                      }
                    />
                    <Route
                      path="/audit-trail"
                      element={
                        <UserManagementProtectedRoute>
                          <GeneralLayout>
                            <AuditTrailPage />
                          </GeneralLayout>
                        </UserManagementProtectedRoute>
                      }
                    />
                    <Route
                      path="/cass-catalog"
                      element={
                        <UserManagementProtectedRoute>
                          <Suspense fallback={LazyFallback}>
                            <CassCatalogPage />
                          </Suspense>
                        </UserManagementProtectedRoute>
                      }
                    />
                    <Route
                      path="/cass-management"
                      element={
                        <UserManagementProtectedRoute>
                          <Suspense fallback={LazyFallback}>
                            <CassManagementPage />
                          </Suspense>
                        </UserManagementProtectedRoute>
                      }
                    />
                    <Route
                      path="/cass-mappings"
                      element={
                        <UserManagementProtectedRoute>
                          <Suspense fallback={LazyFallback}>
                            <CassMappingsPage />
                          </Suspense>
                        </UserManagementProtectedRoute>
                      }
                    />
                    <Route
                      path="/delivered-features"
                      element={
                        <UserManagementProtectedRoute>
                          <GeneralLayout>
                            <Suspense fallback={LazyFallback}>
                              <DeliveredFeaturesPage />
                            </Suspense>
                          </GeneralLayout>
                        </UserManagementProtectedRoute>
                      }
                    />
                    <Route
                      path="/refresh-monitor"
                      element={
                        <UserManagementProtectedRoute>
                          <GeneralLayout>
                            <RefreshMonitorPage />
                          </GeneralLayout>
                        </UserManagementProtectedRoute>
                      }
                    />
                    <Route
                      path="/key-accounts-daily"
                      element={
                        <UserManagementProtectedRoute>
                          <GeneralLayout>
                            <KeyAccountsDailyConfigPage />
                          </GeneralLayout>
                        </UserManagementProtectedRoute>
                      }
                    />
                    <Route
                      path="/qa-dashboard"
                      element={
                        <UserManagementProtectedRoute>
                          <GeneralLayout>
                            <QaDashboardPage />
                          </GeneralLayout>
                        </UserManagementProtectedRoute>
                      }
                    />
                    <Route
                      path="/email-delivery-log"
                      element={
                        <UserManagementProtectedRoute>
                          <GeneralLayout>
                            <EmailDeliveryLogPage />
                          </GeneralLayout>
                        </UserManagementProtectedRoute>
                      }
                    />
                    <Route
                      path="/user-scope-management"
                      element={
                        <UserManagementProtectedRoute>
                          <UserScopeManagementPage />
                        </UserManagementProtectedRoute>
                      }
                    />
                    <Route
                      element={
                        <UserManagementProtectedRoute>
                          <GeneralLayout>
                            <Suspense fallback={LazyFallback}>
                              <AdminShell />
                            </Suspense>
                          </GeneralLayout>
                        </UserManagementProtectedRoute>
                      }
                    >
                      <Route
                        path="/dashboard"
                        element={<AdminSectionPage sectionId="dashboard" />}
                      />
                      <Route
                        path="/user-analytics"
                        element={<AdminSectionPage sectionId="user-analytics" />}
                      />
                      <Route
                        path="/page-analytics"
                        element={<AdminSectionPage sectionId="page-analytics" />}
                      />
                      <Route
                        path="/user-config"
                        element={<AdminSectionPage sectionId="user-config" />}
                      />
                      <Route
                        path="/email-scheduling"
                        element={<AdminSectionPage sectionId="email-scheduling" />}
                      />
                      <Route
                        path="/reports-jobs"
                        element={<AdminSectionPage sectionId="reports-jobs" />}
                      />
                      <Route
                        path="/email-campaigns"
                        element={<AdminSectionPage sectionId="email-campaigns" />}
                      />
                      <Route
                        path="/feedback"
                        element={<AdminSectionPage sectionId="feedback" />}
                      />
                      <Route
                        path="/content-quality"
                        element={<AdminSectionPage sectionId="content-quality" />}
                      />
                      <Route
                        path="/learning-rules"
                        element={<AdminSectionPage sectionId="learning-rules" />}
                      />
                      <Route
                        path="/workflow-visibility"
                        element={<AdminSectionPage sectionId="workflow-visibility" />}
                      />
                      <Route
                        path="/deck-styles"
                        element={<AdminSectionPage sectionId="export-styles" />}
                      />
                      <Route
                        path="/view-as-user"
                        element={<AdminSectionPage sectionId="view-as-user" />}
                      />
                    </Route>
                    <Route
                      path="/superadmin"
                      element={
                        <UserManagementProtectedRoute>
                          <div className="dashai-scope">
                            <Suspense fallback={LazyFallback}>
                              <DashaiSuperAdmin />
                            </Suspense>
                          </div>
                        </UserManagementProtectedRoute>
                      }
                    />
                    <Route
                      path="*"
                      element={<NotFoundRedirect />}
                    />
                  </Routes>
                </ProtectedRoute>
              }
            />
          </Routes>
        <Toaster />
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;
